import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useEmbarqueDependencias } from "contexts/EmbarqueDependenciasContext.tsx";
import { useProfile } from "contexts/ProfileContext.tsx";
import Separator from "core/components/Separator.tsx";
import SelectInputField from "core/data-entry/SelectInputField.tsx";
import Modal from "core/feedback/Modal.tsx";
import { motion } from "framer-motion";
import { Veiculo } from "models/Veiculo.ts";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import RequestControllerRedux from "../../contexts/RequestController.js";
import CustomButtonV2 from "../CustomButtonV2/CustomButtonV2.js";
import ModalCadastro from "../ModalCadastro/ModalCadastro.tsx";
import "../ModalVeiculo/ModalVeiculo.css";
import NewIcon from "../NewIcon/NewIcon.js";
import ToastErro from "../Toast/ToastErro/ToastErro.tsx";

export interface SimpleDialogProps {
	titulo: string;
	tituloBotao: string;
	tituloConfirmar: string;
	texto: string;
	dataAlteracao: string;
	open: boolean;
	listaVeiculos: any[];
	onClose: () => void;
	onClick: () => void;
	setVeiculo: (e: any) => void;
}

function ModalVeiculo(props: SimpleDialogProps) {
	const { setDadosRequestControllerRedux } = useContext(RequestControllerRedux);

	const { profile } = useProfile();

	const { veiculo, fetchVeiculos } = useEmbarqueDependencias();

	const defaultVeiculoOptions = useMemo(() => {
		return veiculo
			.filter((v) => v.caminhao || v.carreta || v.carreta2)
			.sort((a, b) => {
				const placaA = a.caminhao?.placa || "";
				const placaB = b.caminhao?.placa || "";
				return placaA.localeCompare(placaB);
			})
			.slice(0, 15)
			.map((data) => ({
				title: `${data.caminhao?.placa || ""} - ${data.proprietarioVeiculo?.nome ?? ""}`,
				obj: data,
			}));
	}, [veiculo]);

	const { onClose, open, onClick } = props;

	const [valuesSelect, setValuesSelect] = useState<any>([]);

	const [openToastErro, setOpenToastErro] = useState(false);

	const handleCloseToastErro = () => {
		setOpenToastErro(false);
	};

	const [textoErro, setTextoErro] = useState("");

	const handleClick = () => {
		if (valuesSelect.obj?.idVeiculo) {
			onClick();
			clearVeiculo();
		} else {
			setTextoErro("Selecione um veículo para prosseguir.");
			setOpenToastErro(true);
		}
	};

	const [idAtual, setIdAtual] = useState(0);

	const [disable, setDisable] = useState(false);

	const [openCadastroStatus, setOpenCadastroStatus] = useState(false);
	const [veiculoOptions, setVeiculoOptions] = useState(defaultVeiculoOptions);

	const handleCloseCadastroStatus = useCallback(() => {
		setOpenCadastroStatus(false);
		setDadosRequestControllerRedux("Close Status");
		fetchVeiculos(profile?.token ?? "");
	}, [fetchVeiculos, profile?.token, setDadosRequestControllerRedux]);

	const clearVeiculo = useCallback(() => {
		setValuesSelect({
			obj: null,
			title: "",
		});
		props.setVeiculo(null);
	}, [props.setVeiculo]);

	const handleClose = useCallback(() => {
		onClose();
		clearVeiculo();
	}, [onClose, clearVeiculo]);

	const handleVeiculoInputChange = useCallback(
		(inputValue: React.ChangeEvent<HTMLInputElement>) => {
			const normalizedInput = inputValue.target.value.trim().toLowerCase();

			if (normalizedInput === "") {
				setVeiculoOptions([]);
				return;
			}

			const filteredOptions = veiculo
				.filter((veiculo) => {
					const placa = veiculo.caminhao?.placa?.toLowerCase() || "";
					const proprietario =
						veiculo.proprietarioVeiculo?.nome?.toLowerCase() || "";

					return (
						placa.includes(normalizedInput) ||
						proprietario.includes(normalizedInput)
					);
				})
				.map((data) => ({
					title: `${data.caminhao?.placa} - ${data.proprietarioVeiculo?.nome ?? ""}`,
					obj: data,
				}))
				.sort((a, b) => a.title.localeCompare(b.title));

			setVeiculoOptions(filteredOptions);
		},
		[veiculo, setVeiculoOptions],
	);

	const handleVeiculoChange = useCallback(
		(selectedOption: { obj: Veiculo; title: string }) => {
			const selectedOptionData = selectedOption.obj;
			setValuesSelect({
				obj: selectedOptionData,
				title: selectedOption.title,
			});
			props.setVeiculo(selectedOptionData.idVeiculo);
		},
		[props],
	);

	useEffect(() => {
		setVeiculoOptions(defaultVeiculoOptions);
	}, [defaultVeiculoOptions]);

	return (
		<Modal isOpen={open} onClose={handleClose} closeButton>
			<motion.div
				initial={{ opacity: 0, y: 50 }}
				exit={{ opacity: 0, y: 50 }}
				animate={{
					opacity: 1,
					y: 0,
				}}
				layout
				transition={{ duration: 0.3 }}
				onClick={(e) => e.stopPropagation()}
				className="div-modal-ve"
			>
				<DialogTitle id="alert-dialog-title">{props.titulo}</DialogTitle>
				<DialogContentText id="alert-dialog-description">
					<div className="usuario-data-row">
						<div className="input-veiculo-wrapper">
							<SelectInputField
								label="Veículo:"
								placeholder="Digite para buscar."
								options={veiculoOptions}
								value={valuesSelect.title || ""}
								onChange={handleVeiculoChange}
								onInputChange={handleVeiculoInputChange}
								onClear={clearVeiculo}
							/>
							<div>
								<NewIcon
									onClick={() => {
										setOpenCadastroStatus(true);
										setIdAtual(0);
										setDisable(false);
									}}
								/>
								<Separator size="2xs" />
							</div>
						</div>
					</div>
				</DialogContentText>
				<Separator size="xl" />
				<DialogActions>
					<CustomButtonV2
						classCss="custom-button-cadastrar"
						classText="button-texto-cadastrar"
						titulo="OK"
						cor="#0946B8"
						onClick={handleClick}
					/>
					<CustomButtonV2
						classCss="custom-button-cancelar"
						classText="button-texto-cancelar"
						titulo="Cancelar"
						cor="#0946B8"
						onClick={handleClose}
					/>
				</DialogActions>
				<ModalCadastro
					titulo="Cadastro"
					tituloConfirmar="OK"
					cadastroVeiculo="sim"
					open={openCadastroStatus}
					idAtual={idAtual}
					desabilitar={disable}
					onClose={handleCloseCadastroStatus}
				/>
				<ToastErro
					texto={textoErro}
					open={openToastErro}
					onClose={handleCloseToastErro}
				/>
			</motion.div>
		</Modal>
	);
}

export default ModalVeiculo;
